import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { followUpAPI } from '@/lib/api';
import { MessageSquare, CheckCircle, Clock, User, Sparkles } from 'lucide-react';

export function FollowUpPage() {
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'sent'>('pending');

  const { data: followUpsData, isLoading } = useQuery({
    queryKey: ['follow-ups', filter],
    queryFn: () => followUpAPI.getFollowUps(filter === 'all' ? undefined : { status: filter }),
  });

  const handleApprove = (id: string) => {
    // TODO: Implement approval logic
    console.log('Approve follow-up', id);
  };

  const handleReject = (id: string) => {
    // TODO: Implement rejection logic
    console.log('Reject follow-up', id);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Follow-up Management</h1>
        <p className="text-muted-foreground">Review AI-generated follow-up suggestions</p>
      </div>

      {/* Filter Tabs */}
      <div className="border-b">
        <nav className="-mb-px flex space-x-8">
          {[
            { key: 'pending', label: 'Pending Review', count: 8 },
            { key: 'approved', label: 'Approved', count: 15 },
            { key: 'sent', label: 'Sent', count: 42 },
            { key: 'all', label: 'All', count: 65 },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key as any)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                filter === tab.key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
              }`}
            >
              {tab.label}
              <span className="ml-2 py-0.5 px-2 rounded-full text-xs bg-muted">
                {tab.count}
              </span>
            </button>
          ))}
        </nav>
      </div>

      {/* Follow-ups List */}
      <div className="space-y-4">
        {[
          {
            id: '1',
            leadName: 'John Smith',
            leadCompany: 'TechCorp Inc.',
            lastContact: '3 days ago',
            aiSuggestion: 'Follow up with case study mention based on their industry focus',
            suggestedMessage: 'Hi John, I hope you\'re doing well. I wanted to follow up on our conversation about digital transformation. I recently helped a similar company in the tech space increase their lead conversion by 45%. Would you be interested in a brief call to discuss how this might apply to TechCorp?',
            status: 'pending',
            confidence: 85,
          },
          {
            id: '2',
            leadName: 'Sarah Johnson',
            leadCompany: 'Marketing Pro',
            lastContact: '1 week ago',
            aiSuggestion: 'Send value-add content related to marketing automation',
            suggestedMessage: 'Hi Sarah, I came across this interesting article about marketing automation trends for 2025 and thought you might find it valuable. Based on our previous discussion about scaling your campaigns, this could provide some useful insights.',
            status: 'pending',
            confidence: 92,
          },
          {
            id: '3',
            leadName: 'Mike Davis',
            leadCompany: 'Growth Startup',
            lastContact: '5 days ago',
            aiSuggestion: 'Reference their recent company news and relate to services',
            suggestedMessage: 'Hi Mike, Congratulations on your recent Series A funding! I saw the announcement and wanted to reach out. As you scale your operations, lead generation becomes even more critical. Would you be open to a brief discussion about how we\'ve helped other funded startups accelerate their growth?',
            status: 'approved',
            confidence: 78,
          },
        ].map((followUp) => (
          <Card key={followUp.id}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <User className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <CardTitle className="text-lg">{followUp.leadName}</CardTitle>
                    <p className="text-sm text-muted-foreground">{followUp.leadCompany}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="flex items-center space-x-1 text-sm text-muted-foreground">
                    <Sparkles className="h-4 w-4" />
                    <span>{followUp.confidence}% confidence</span>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    followUp.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    followUp.status === 'approved' ? 'bg-green-100 text-green-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                    {followUp.status}
                  </span>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span>Last contact: {followUp.lastContact}</span>
              </div>

              <div className="bg-blue-50 p-3 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <Sparkles className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-800">AI Suggestion</span>
                </div>
                <p className="text-sm text-blue-700">{followUp.aiSuggestion}</p>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Suggested Message</label>
                <div className="bg-muted p-3 rounded-lg">
                  <p className="text-sm">{followUp.suggestedMessage}</p>
                </div>
              </div>

              {followUp.status === 'pending' && (
                <div className="flex justify-end space-x-2">
                  <Button
                    variant="outline"
                    onClick={() => handleReject(followUp.id)}
                  >
                    Reject
                  </Button>
                  <Button
                    onClick={() => handleApprove(followUp.id)}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Approve & Send
                  </Button>
                </div>
              )}

              {followUp.status === 'approved' && (
                <div className="flex justify-end">
                  <div className="flex items-center text-sm text-green-600">
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Approved - will send at optimal time
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}

        {(!followUpsData?.data || (followUpsData.data as any)?.length === 0) && (
          <Card>
            <CardContent className="text-center py-8">
              <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No follow-ups found</h3>
              <p className="text-muted-foreground">
                {filter === 'pending' 
                  ? 'All follow-ups have been reviewed'
                  : 'Follow-up suggestions will appear here as AI analyzes your lead interactions'
                }
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}